/**
 * automation.ts — optional managers. Every flag is OFF by default and the whole
 * tick is a no-op until the player opts in, so determinism (and the soak test)
 * is unchanged. None of this draws RNG: nugget claim is RNG-free, and
 * prestige/ascend carry the RNG stream — so enabling automation stays deterministic.
 */
import type { GameState } from '../core/GameState';
import type { TickContext } from '../core/GameLoop';
import { BUILDINGS, WORKERS, UPGRADES } from '../data';
import { claimNugget } from './nuggets';
import { prestigeGain, doPrestige } from './prestige';
import { ascensionGain, doAscend } from './ascension';
import {
  buyUpgrade, upgradeUnlocked, buyBuilding, buildingUnlocked, buildingCost,
  hireWorker, workerUnlocked, workerCost, upgradeTool, nextToolCost,
} from './shop';

const AUTOBUY_INTERVAL = 1000; // ms between auto-buy passes

/** Buy affordable upgrades + the single cheapest affordable building/worker + tool. */
function autoBuyStep(state: GameState): void {
  for (const u of UPGRADES) if (upgradeUnlocked(state, u.id)) buyUpgrade(state, u.id);

  const toolCost = nextToolCost(state);
  if (toolCost !== null && state.economy.money >= toolCost) upgradeTool(state);

  // Cheapest affordable building.
  let bestB: string | null = null;
  let bestBCost = Infinity;
  for (const b of BUILDINGS) {
    if (!buildingUnlocked(state, b.id)) continue;
    const c = buildingCost(state, b.id, 1);
    if (c <= state.economy.money && c < bestBCost) { bestBCost = c; bestB = b.id; }
  }
  if (bestB) buyBuilding(state, bestB, 1);

  // Cheapest affordable worker.
  let bestW: string | null = null;
  let bestWCost = Infinity;
  for (const w of WORKERS) {
    if (!workerUnlocked(state, w.id)) continue;
    const c = workerCost(state, w.id, 1);
    if (c <= state.economy.money && c < bestWCost) { bestWCost = c; bestW = w.id; }
  }
  if (bestW) hireWorker(state, bestW, 1);
}

export function tickAutomation(state: GameState, ctx: TickContext): void {
  const a = state.automation;
  if (a.autoNugget && state.nugget.active) claimNugget(state, ctx.bus);

  if (a.autoBuy && state.meta.gameTimeMs - a.lastAutoBuyAt >= AUTOBUY_INTERVAL) {
    a.lastAutoBuyAt = state.meta.gameTimeMs;
    autoBuyStep(state);
  }

  if (a.autoPrestige && prestigeGain(state) >= a.autoPrestigeThreshold) {
    doPrestige(state, ctx.clock.now(), ctx.bus);
  }
  if (a.autoAscend && ascensionGain(state) >= a.autoAscendThreshold) {
    doAscend(state, ctx.clock.now(), ctx.bus);
  }
}
